import Phaser from 'phaser';
import { socketClient } from '../net/SocketClient';
import { getClientId } from '../net/clientId';
import { startGameScene } from './startGame';
import { scenarioCheckboxListHTML, SCENARIO_LIST } from '../data/scenarioList';
import { computeStandings, scheduleSize } from '~shared/tournament-logic';
import type {
  ChatMessage, GameState, Side, Tournament, TournamentMatch,
  TournamentSummary, OngoingTournamentInfo,
} from '~shared/types';

type BrowseTab = 'create' | 'join' | 'mine';

export class TournamentLobbyScene extends Phaser.Scene {
  constructor() { super({ key: 'TournamentLobbyScene' }); }

  private form!: Phaser.GameObjects.DOMElement;
  private isTouch = false;
  private view: 'browse' | 'room' = 'browse';
  private browseTab: BrowseTab = 'create';
  private tournament: Tournament | null = null;
  private viewingTournamentId: string | null = null;
  private myClientId = getClientId();
  private selectedOpenTournamentId: string | null = null;

  create(): void {
    this.events.once('shutdown', this.shutdown, this);
    this.isTouch = this.sys.game.device.input.touch;

    const vp = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (vp) vp.content = 'width=device-width, initial-scale=1.0';

    const W = 1200, H = 768;
    this.add.rectangle(W / 2, H / 2, W, H, 0x1a2d1a);
    this.add.text(W / 2, 60, "MEMOIR '44", {
      fontSize: this.isTouch ? '72px' : '52px', color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.add.text(W / 2, 115, 'Tournament Mode', {
      fontSize: this.isTouch ? '28px' : '20px', color: '#aaaaaa',
    }).setOrigin(0.5);

    // Top-anchored (not centered) so tall/variable content (e.g. the dashboard's
    // leaderboard + match lists) grows downward into its own scroll area instead
    // of overflowing the canvas in both directions.
    this.form = this.add.dom(W / 2, 145).setOrigin(0.5, 0).createFromHTML(this.buildBrowseHTML());
    this.form.addListener('click');
    this.form.on('click', (e: MouseEvent) => this.onClick(e));
    this.form.addListener('keydown');
    this.form.on('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (this.browseTab === 'create') this.handleCreate();
      if (this.browseTab === 'join')   this.handleJoinByCode();
    });

    const socket = socketClient.connect(window.location.origin);
    socket.on('tournaments_updated', (list) => this.updateOpenTournamentsList(list));
    socket.on('your_tournaments_updated', (list) => this.updateMyTournamentsList(list));
    socket.on('tournament_state', (t) => {
      if (t.id !== this.viewingTournamentId) return;
      this.tournament = t;
      if (this.view === 'room') this.renderRoom();
    });
    socket.on('error', (msg) => this.showGlobalStatus(msg));

    const requestLists = () => socket.emit('list_tournaments', { clientId: this.myClientId });
    if (socket.connected) requestLists();
    else socket.once('connect', requestLists);
  }

  shutdown(): void {
    const s = socketClient.socket;
    s.off('tournaments_updated');
    s.off('your_tournaments_updated');
    s.off('tournament_state');
    s.off('error');
    s.off('connect');
  }

  // ── Click routing ────────────────────────────────────────────────────────────

  private onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const id = target.id;

    if (id === 'tlHomeBtn') { this.scene.start('LobbyScene'); return; }
    if (id === 'tlTabCreate') { this.switchBrowseTab('create'); return; }
    if (id === 'tlTabJoin')   { this.switchBrowseTab('join'); return; }
    if (id === 'tlTabMine')   { this.switchBrowseTab('mine'); return; }
    if (id === 'tlCreateBtn') { this.handleCreate(); return; }
    if (id === 'tlJoinCodeBtn') { this.handleJoinByCode(); return; }
    if (id === 'tlJoinSelectedBtn') { this.handleJoinSelected(); return; }
    if (id === 'tlBackBtn') { this.goToBrowse(); return; }
    if (id === 'tlStartBtn') { this.handleStart(); return; }
    if (id === 'tlEndEarlyBtn') { this.handleEndEarly(); return; }

    if (target.classList.contains('scenario-checkbox')) { this.recalcCreatePreview(); return; }
    if (target.classList.contains('tl-open-row')) { this.selectOpenTournamentRow(target); return; }
    if (target.classList.contains('tl-resume-btn')) { this.handleResume(target.dataset['tid']!); return; }
    if (target.classList.contains('tl-play-btn')) { this.handlePlayMatch(target.dataset['matchid']!); return; }
  }

  private switchBrowseTab(tab: BrowseTab): void {
    this.browseTab = tab;
    this.renderBrowse();
  }

  private goToBrowse(): void {
    this.tournament = null;
    this.viewingTournamentId = null;
    this.view = 'browse';
    this.renderBrowse();
    const socket = socketClient.socket;
    socket.emit('list_tournaments', { clientId: this.myClientId });
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  private handleCreate(): void {
    const node = this.form.node as HTMLElement;
    const name       = (node.querySelector('#tlCreateName') as HTMLInputElement).value.trim();
    const tName      = (node.querySelector('#tlCreateTournamentName') as HTMLInputElement).value.trim();
    const scenarioIds = [...node.querySelectorAll<HTMLInputElement>('.scenario-checkbox:checked')].map(cb => cb.value);

    if (!name) { this.showGlobalStatus('Please enter your name.'); return; }
    if (scenarioIds.length === 0) { this.showGlobalStatus('Select at least 1 scenario.'); return; }

    this.viewingTournamentId = null; // set once tournament_state confirms the new id
    const socket = socketClient.connect(window.location.origin);
    const doCreate = () => {
      socket.emit('create_tournament', { name: tName, scenarioIds, playerName: name, clientId: this.myClientId });
    };
    socket.once('tournament_state', (t) => {
      this.viewingTournamentId = t.id;
      this.tournament = t;
      this.view = 'room';
      this.renderRoom();
    });
    if (socket.connected) doCreate();
    else socket.once('connect', doCreate);
  }

  private handleJoinByCode(): void {
    const node = this.form.node as HTMLElement;
    const name = (node.querySelector('#tlJoinName') as HTMLInputElement).value.trim();
    const code = (node.querySelector('#tlJoinCode') as HTMLInputElement).value.trim().toUpperCase();
    if (!name) { this.showGlobalStatus('Please enter your name.'); return; }
    if (!code) { this.showGlobalStatus('Enter a tournament code.'); return; }
    this.joinTournament(code, name);
  }

  private handleJoinSelected(): void {
    const node = this.form.node as HTMLElement;
    const name = (node.querySelector('#tlJoinName') as HTMLInputElement).value.trim();
    if (!name) { this.showGlobalStatus('Please enter your name.'); return; }
    if (!this.selectedOpenTournamentId) { this.showGlobalStatus('Select a tournament from the list first.'); return; }
    this.joinTournament(this.selectedOpenTournamentId, name);
  }

  private joinTournament(tournamentId: string, playerName: string): void {
    this.viewingTournamentId = tournamentId;
    const socket = socketClient.connect(window.location.origin);
    const doJoin = () => socket.emit('join_tournament', { tournamentId, playerName, clientId: this.myClientId });
    socket.once('tournament_state', (t) => {
      if (t.id !== tournamentId) return;
      this.tournament = t;
      this.view = 'room';
      this.renderRoom();
    });
    if (socket.connected) doJoin();
    else socket.once('connect', doJoin);
  }

  private handleResume(tournamentId: string): void {
    this.viewingTournamentId = tournamentId;
    const socket = socketClient.connect(window.location.origin);
    socket.once('tournament_state', (t) => {
      if (t.id !== tournamentId) return;
      this.tournament = t;
      this.view = 'room';
      this.renderRoom();
    });
    socket.emit('get_tournament', { tournamentId });
  }

  private handleStart(): void {
    if (!this.tournament) return;
    socketClient.socket.emit('start_tournament', { tournamentId: this.tournament.id, clientId: this.myClientId });
  }

  private handleEndEarly(): void {
    if (!this.tournament) return;
    socketClient.socket.emit('end_tournament_early', { tournamentId: this.tournament.id, clientId: this.myClientId });
  }

  private handlePlayMatch(matchId: string): void {
    if (!this.tournament) return;
    const match = this.tournament.matches.find(m => m.id === matchId);
    if (!match) return;
    const mine = match.playerA.clientId === this.myClientId ? match.playerA : match.playerB;
    const tournamentId = this.tournament.id;

    const socket = socketClient.connect(window.location.origin);
    let myAssignedSide: Side | null = null;
    let chatHistory: ChatMessage[] = [];
    socket.once('chat_history', (messages) => { chatHistory = messages; });

    const onPlayerJoined = (info: { side: Side; playerCount: number }) => {
      myAssignedSide = info.side;
      socketClient.mySide = info.side;
    };
    socket.on('player_joined', onPlayerJoined);

    const transition = (state: GameState) => {
      socket.off('game_state', onGameState);
      socket.off('game_start', transition);
      socket.off('player_joined', onPlayerJoined);
      const mySide = myAssignedSide ?? mine.side;
      startGameScene(this, { gameState: state, mySide, roomId: state.roomId, playerName: mine.playerName, chatHistory });
    };
    const onGameState = (state: GameState) => { if (state.phase !== 'waiting') transition(state); };

    socket.on('game_state', onGameState);
    socket.once('game_start', transition);
    socket.emit('play_tournament_match', { tournamentId, matchId, clientId: this.myClientId });
  }

  private recalcCreatePreview(): void {
    const node = this.form.node as HTMLElement;
    const count = node.querySelectorAll('.scenario-checkbox:checked').length;
    const previewEl = node.querySelector('#tlCreatePreview') as HTMLElement | null;
    if (!previewEl) return;
    // Preview assumes 2 players joined so far; grows as more players join before start.
    const games = scheduleSize(2, count);
    previewEl.textContent = count === 0
      ? 'Select scenarios to see how many games this will generate.'
      : `${count} scenario${count === 1 ? '' : 's'} selected → ${games} games with 2 players (scales up as more join: players × (players−1) × scenarios).`;
  }

  private selectOpenTournamentRow(row: HTMLElement): void {
    const node = this.form.node as HTMLElement;
    node.querySelectorAll('.tl-open-row').forEach(r => r.classList.remove('selected'));
    row.classList.add('selected');
    row.style.borderColor = '#d4af37';
    this.selectedOpenTournamentId = row.dataset['tid']!;
  }

  private showGlobalStatus(msg: string): void {
    const node = this.form.node as HTMLElement;
    const el = node.querySelector('#tlGlobalStatus') as HTMLElement | null;
    if (el) el.textContent = msg;
  }

  // ── Rendering ────────────────────────────────────────────────────────────────

  private style() {
    const isTouch = this.isTouch;
    return {
      lblSz:   isTouch ? '18px' : '12px',
      inputSz: isTouch ? '24px' : '15px',
      inputPd: isTouch ? '14px 16px' : '9px 12px',
      btnSz:   isTouch ? '24px' : '17px',
      btnPd:   isTouch ? '18px 0' : '12px 0',
      tabSz:   isTouch ? '22px' : '15px',
      tabPd:   isTouch ? '16px' : '10px',
      formW:   isTouch ? '1050px' : '760px',
    };
  }

  private inputStyle(): string {
    const s = this.style();
    return `padding:${s.inputPd}; width:100%; box-sizing:border-box; font-size:${s.inputSz};
            border-radius:5px; border:1px solid #555; background:#152a15; color:#eee; outline:none;`;
  }

  private buildBrowseHTML(): string {
    const s = this.style();
    const inp = this.inputStyle();
    return `
    <div style="font-family:Arial,sans-serif; color:#eee; width:${s.formW};">
      <div style="display:flex; margin-bottom:0; gap:4px;">
        <button id="tlTabCreate" style="flex:1; padding:${s.tabPd}; font-size:${s.tabSz}; cursor:pointer;
          background:${this.browseTab === 'create' ? '#2a4a2a' : '#1e3a1e'}; color:${this.browseTab === 'create' ? '#d4af37' : '#aaa'};
          border:1px solid #3a6a3a; border-bottom:none; border-radius:6px 6px 0 0; font-weight:bold;">Create</button>
        <button id="tlTabJoin" style="flex:1; padding:${s.tabPd}; font-size:${s.tabSz}; cursor:pointer;
          background:${this.browseTab === 'join' ? '#2a4a2a' : '#1e3a1e'}; color:${this.browseTab === 'join' ? '#d4af37' : '#aaa'};
          border:1px solid #3a6a3a; border-bottom:none; border-radius:6px 6px 0 0;">Join</button>
        <button id="tlTabMine" style="flex:1; padding:${s.tabPd}; font-size:${s.tabSz}; cursor:pointer;
          background:${this.browseTab === 'mine' ? '#2a4a2a' : '#1e3a1e'}; color:${this.browseTab === 'mine' ? '#d4af37' : '#aaa'};
          border:1px solid #3a6a3a; border-bottom:none; border-radius:6px 6px 0 0;">My Tournaments</button>
      </div>

      <div style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:0 0 6px 6px; padding:20px; min-height:340px; max-height:480px; overflow-y:auto;">
        ${this.browseTab === 'create' ? `
          <div style="display:flex; gap:16px; flex-wrap:wrap;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Your Name</label>
              <input id="tlCreateName" type="text" maxlength="20" placeholder="e.g. Monty" style="${inp}">
            </div>
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Tournament Name</label>
              <input id="tlCreateTournamentName" type="text" maxlength="40" placeholder="e.g. Friday Night 44" style="${inp}">
            </div>
          </div>
          <div style="margin-top:14px;">
            <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Scenarios</label>
            <div style="background:#0a1a0a; border:1px solid #2a4a2a; border-radius:5px; padding:8px 12px; max-height:180px; overflow-y:auto;">
              ${scenarioCheckboxListHTML('tlSc')}
            </div>
          </div>
          <p id="tlCreatePreview" style="margin-top:10px; color:#88aa88; font-size:${s.lblSz};">
            Select scenarios to see how many games this will generate.
          </p>
          <button id="tlCreateBtn" style="margin-top:8px; padding:${s.btnPd}; width:100%; font-size:${s.btnSz}; cursor:pointer;
            background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">Create Tournament</button>
        ` : ''}

        ${this.browseTab === 'join' ? `
          <div style="display:flex; gap:16px; flex-wrap:wrap; margin-bottom:12px;">
            <div style="flex:1; min-width:180px;">
              <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Your Name</label>
              <input id="tlJoinName" type="text" maxlength="20" placeholder="e.g. Rommel" style="${inp}">
            </div>
            <div style="flex:1; min-width:140px;">
              <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Join by Code</label>
              <div style="display:flex; gap:8px;">
                <input id="tlJoinCode" type="text" maxlength="8" placeholder="ABC123"
                  style="${inp} letter-spacing:2px; text-transform:uppercase;">
                <button id="tlJoinCodeBtn" style="padding:0 16px; font-size:${s.lblSz}; cursor:pointer;
                  background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold; white-space:nowrap;">Join</button>
              </div>
            </div>
          </div>
          <label style="display:block; margin-bottom:4px; font-size:${s.lblSz}; color:#aaa;">Open Tournaments</label>
          <div id="tlOpenList" style="background:#0a1a0a; border:1px solid #2a4a2a; border-radius:5px; padding:8px; min-height:90px; max-height:180px; overflow-y:auto;">
            <div style="color:#888; font-size:${s.lblSz}; text-align:center; padding:20px 0;">Searching for open tournaments…</div>
          </div>
          <button id="tlJoinSelectedBtn" style="margin-top:12px; padding:${s.btnPd}; width:100%; font-size:${s.btnSz}; cursor:pointer;
            background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">Join Selected Tournament</button>
        ` : ''}

        ${this.browseTab === 'mine' ? `
          <div id="tlMineList" style="min-height:120px;">
            <div style="color:#888; font-size:${s.lblSz}; text-align:center; padding:30px 0;">Loading your tournaments…</div>
          </div>
        ` : ''}
      </div>

      <p id="tlGlobalStatus" style="margin-top:10px; color:#e08080; font-size:${s.lblSz}; min-height:18px; text-align:center;"></p>
      <div style="margin-top:8px; text-align:center;">
        <button id="tlHomeBtn" style="padding:6px 14px; font-size:12px; background:#243824;
          color:#aabbaa; border:1px solid #3a6a3a; border-radius:4px; cursor:pointer;
          font-family:Arial,sans-serif;">← Main Lobby</button>
      </div>
    </div>`;
  }

  private renderBrowse(): void {
    (this.form.node as HTMLElement).innerHTML = this.buildBrowseHTML();
    if (this.browseTab === 'mine') socketClient.socket.emit('list_tournaments', { clientId: this.myClientId });
  }

  private updateOpenTournamentsList(list: TournamentSummary[]): void {
    const node = this.form.node as HTMLElement;
    const el = node.querySelector('#tlOpenList') as HTMLElement | null;
    if (!el) return;
    if (list.length === 0) {
      el.innerHTML = `<div style="color:#666; font-size:13px; text-align:center; padding:20px 0;">No open tournaments — create one!</div>`;
      return;
    }
    el.innerHTML = list.map(t => `
      <div class="tl-open-row" data-tid="${t.id}" style="
        background:#152a15; border:1px solid #2a4a2a; border-radius:5px; padding:8px 12px; margin-bottom:6px;
        cursor:pointer; display:flex; justify-content:space-between; align-items:center;">
        <span style="font-size:13px;">
          <strong style="color:#d4af37;">${escapeHtml(t.name)}</strong>
          <span style="color:#888;"> — host ${escapeHtml(t.hostName)} · ${t.participantCount} joined · ${t.scenarioCount} scenarios</span>
        </span>
        <span style="color:#556655; font-size:11px; letter-spacing:1px;">${t.id}</span>
      </div>`).join('');
  }

  private updateMyTournamentsList(list: OngoingTournamentInfo[]): void {
    const node = this.form.node as HTMLElement;
    const el = node.querySelector('#tlMineList') as HTMLElement | null;
    if (!el) return;
    if (list.length === 0) {
      el.innerHTML = `<div style="color:#888; font-size:13px; text-align:center; padding:30px 0;">You're not in any tournaments yet.</div>`;
      return;
    }
    el.innerHTML = list.map(t => {
      const statusLabel = t.status === 'lobby' ? 'Waiting to start'
        : t.status === 'completed' ? 'Completed'
        : `${t.matchesCompleted}/${t.matchesTotal} matches played`;
      return `
      <div style="background:#152a15; border:1px solid #d4af37; border-radius:6px; padding:10px 16px; margin-bottom:8px;
        display:flex; justify-content:space-between; align-items:center; gap:12px;">
        <span style="color:#d4af37; font-size:13px;">
          🏆 <strong>${escapeHtml(t.name)}</strong> — ${t.participantCount} players — ${statusLabel}
        </span>
        <button class="tl-resume-btn" data-tid="${t.id}" style="padding:6px 14px; background:#d4af37; color:#111;
          border:none; border-radius:4px; cursor:pointer; font-weight:bold; font-size:13px; flex-shrink:0;">Open</button>
      </div>`;
    }).join('');
  }

  private buildRoomHTML(): string {
    const t = this.tournament;
    if (!t) return '';
    const s = this.style();
    const isHost = t.hostClientId === this.myClientId;
    return t.status === 'lobby' ? this.buildLobbyRoomHTML(t, isHost, s) : this.buildDashboardHTML(t, isHost, s);
  }

  private buildLobbyRoomHTML(t: Tournament, isHost: boolean, s: ReturnType<TournamentLobbyScene['style']>): string {
    const scenarioNames = t.scenarioIds.map(id => SCENARIO_LIST.find(x => x.id === id)?.name ?? id).join(', ');
    const preview = scheduleSize(t.participants.length, t.scenarioIds.length);
    return `
    <div style="font-family:Arial,sans-serif; color:#eee; width:${s.formW};">
      <div style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:6px; padding:20px; text-align:center; max-height:520px; overflow-y:auto;">
        <p style="color:#88aa88; font-size:${s.lblSz}; margin:0 0 6px;">Tournament code — share with players:</p>
        <div style="font-size:32px; font-weight:bold; letter-spacing:8px; color:#fff;
          background:#0a1a0a; border:1px solid #d4af37; border-radius:6px; padding:10px 24px; display:inline-block; margin-bottom:14px;">
          ${t.id}
        </div>
        <h3 style="color:#d4af37; margin:0 0 4px;">${escapeHtml(t.name)}</h3>
        <p style="color:#aaa; font-size:${s.lblSz}; margin:0 0 14px;">Scenarios: ${escapeHtml(scenarioNames)}</p>

        <div style="text-align:left; background:#152a15; border:1px solid #2a4a2a; border-radius:5px; padding:10px 16px; margin-bottom:14px; max-height:180px; overflow-y:auto;">
          <p style="color:#88aa88; font-size:${s.lblSz}; margin:0 0 6px;">Players joined (${t.participants.length}):</p>
          ${t.participants.map(p => `<div style="font-size:${s.lblSz}; color:#eee;">
            • ${escapeHtml(p.playerName)}${p.clientId === t.hostClientId ? ' <span style="color:#d4af37;">(host)</span>' : ''}
          </div>`).join('')}
        </div>

        <p style="color:#888; font-size:${s.lblSz}; margin:0 0 14px;">
          → ${t.participants.length} players × ${t.scenarioIds.length} scenarios = ${preview} games will be generated
        </p>

        ${isHost ? `
          <button id="tlStartBtn" ${t.participants.length < 2 ? 'disabled' : ''} style="padding:${s.btnPd}; width:100%; font-size:${s.btnSz};
            cursor:${t.participants.length < 2 ? 'not-allowed' : 'pointer'}; opacity:${t.participants.length < 2 ? '0.5' : '1'};
            background:#d4af37; color:#1a1a1a; border:none; border-radius:5px; font-weight:bold;">
            ${t.participants.length < 2 ? 'Waiting for at least 2 players…' : 'Start Tournament'}
          </button>
        ` : `<p style="color:#d4af37; font-size:${s.btnSz};">Waiting for the host to start…</p>`}
      </div>
      <p id="tlGlobalStatus" style="margin-top:10px; color:#e08080; font-size:${s.lblSz}; min-height:18px; text-align:center;"></p>
      <div style="margin-top:8px; text-align:center;">
        <button id="tlBackBtn" style="padding:6px 14px; font-size:12px; background:#243824;
          color:#aabbaa; border:1px solid #3a6a3a; border-radius:4px; cursor:pointer; font-family:Arial,sans-serif;">← Back</button>
      </div>
    </div>`;
  }

  private buildDashboardHTML(t: Tournament, isHost: boolean, s: ReturnType<TournamentLobbyScene['style']>): string {
    const standings = computeStandings(t);
    const completed = t.matches.filter(m => m.status === 'completed').length;

    const rows = standings.map((row, i) => `
      <tr style="${row.clientId === this.myClientId ? 'background:#2a4a2a;' : ''}">
        <td style="padding:5px 8px; text-align:center; color:#888;">${i + 1}</td>
        <td style="padding:5px 8px; color:${row.clientId === this.myClientId ? '#d4af37' : '#eee'}; font-weight:${row.clientId === this.myClientId ? 'bold' : 'normal'};">${escapeHtml(row.playerName)}</td>
        <td style="padding:5px 8px; text-align:center;">${row.played}</td>
        <td style="padding:5px 8px; text-align:center; color:#7fd47f;">${row.wins}</td>
        <td style="padding:5px 8px; text-align:center; color:#e08080;">${row.losses}</td>
        <td style="padding:5px 8px; text-align:center;">${row.medalsFor}</td>
        <td style="padding:5px 8px; text-align:center;">${row.medalsAgainst}</td>
        <td style="padding:5px 8px; text-align:center; font-weight:bold; color:#d4af37;">${row.points}</td>
      </tr>`).join('');

    const matchRow = (m: TournamentMatch, showBoth: boolean) => {
      const mine = m.playerA.clientId === this.myClientId ? m.playerA
                 : m.playerB.clientId === this.myClientId ? m.playerB : null;
      const opp  = mine ? (mine === m.playerA ? m.playerB : m.playerA) : null;
      const label = showBoth
        ? `${escapeHtml(m.playerA.playerName)} (${sideIcon(m.playerA.side)}) vs ${escapeHtml(m.playerB.playerName)} (${sideIcon(m.playerB.side)})`
        : `vs ${escapeHtml(opp!.playerName)} — you: ${sideIcon(mine!.side)} ${mine!.side}`;

      let statusText: string;
      if (m.status === 'completed') {
        if (mine) {
          statusText = m.result!.winnerClientId === mine.clientId ? '✓ Won' : '✗ Lost';
        } else {
          const winnerName = m.result!.winnerClientId === m.playerA.clientId ? m.playerA.playerName : m.playerB.playerName;
          statusText = `✓ ${winnerName} won`;
        }
      } else if (t.status !== 'active') {
        statusText = 'Tournament ended — not played';
      } else if (m.status === 'in-progress') {
        statusText = 'In progress';
      } else {
        statusText = 'Not started';
      }
      // Once the tournament itself has ended (e.g. host ended it early), no
      // further matches may be started or resumed, even if individually pending.
      const canPlay = mine && m.status !== 'completed' && t.status === 'active';
      return `
      <div style="display:flex; justify-content:space-between; align-items:center; gap:10px;
        background:#152a15; border:1px solid #2a4a2a; border-radius:5px; padding:7px 12px; margin-bottom:6px; font-size:12px;">
        <span style="color:#ccc;">${escapeHtml(m.scenarioName)} · ${label}</span>
        <span style="display:flex; align-items:center; gap:8px; flex-shrink:0;">
          <span style="color:${m.status === 'completed' ? '#d4af37' : '#888'};">${statusText}</span>
          ${canPlay ? `<button class="tl-play-btn" data-matchid="${m.id}" style="padding:4px 12px; background:#d4af37; color:#111;
            border:none; border-radius:4px; cursor:pointer; font-weight:bold; font-size:12px;">${m.status === 'in-progress' ? 'Resume' : 'Play'}</button>` : ''}
        </span>
      </div>`;
    };

    const myMatches  = t.matches.filter(m => m.playerA.clientId === this.myClientId || m.playerB.clientId === this.myClientId);
    const allMatches = t.matches;

    return `
    <div style="font-family:Arial,sans-serif; color:#eee; width:${s.formW};">
      <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
        <h3 style="color:#d4af37; margin:0;">${escapeHtml(t.name)}</h3>
        <span style="color:#888; font-size:12px;">${completed}/${t.matches.length} games completed${t.status === 'completed' ? ' — FINAL' : ''}</span>
      </div>

      <div style="max-height:460px; overflow-y:auto; padding-right:4px;">
        <div style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:6px; padding:14px; margin-bottom:12px;">
          <p style="color:#88aa88; font-size:${s.lblSz}; margin:0 0 8px;">Leaderboard</p>
          <div style="overflow-x:auto;">
            <table style="width:100%; border-collapse:collapse; font-size:${s.lblSz};">
              <thead>
                <tr style="color:#888; text-align:center;">
                  <th style="padding:5px 8px;">#</th><th style="text-align:left; padding:5px 8px;">Player</th>
                  <th style="padding:5px 8px;">P</th><th style="padding:5px 8px;">W</th><th style="padding:5px 8px;">L</th>
                  <th style="padding:5px 8px;">MF</th><th style="padding:5px 8px;">MA</th><th style="padding:5px 8px;">Pts</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </div>
        </div>

        <div style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:6px; padding:14px; margin-bottom:12px;">
          <p style="color:#88aa88; font-size:${s.lblSz}; margin:0 0 8px;">Your Matches</p>
          <div>
            ${myMatches.length ? myMatches.map(m => matchRow(m, false)).join('') : `<p style="color:#666; font-size:${s.lblSz};">No matches for you in this tournament.</p>`}
          </div>
        </div>

        <details style="background:#1e3a1e; border:1px solid #3a6a3a; border-radius:6px; padding:14px; margin-bottom:12px;">
          <summary style="color:#88aa88; font-size:${s.lblSz}; cursor:pointer;">All Matches (${allMatches.length})</summary>
          <div style="margin-top:8px;">
            ${allMatches.map(m => matchRow(m, true)).join('')}
          </div>
        </details>
      </div>

      <p id="tlGlobalStatus" style="margin-top:4px; color:#e08080; font-size:${s.lblSz}; min-height:18px; text-align:center;"></p>
      <div style="margin-top:4px; text-align:center; display:flex; justify-content:center; gap:10px;">
        <button id="tlBackBtn" style="padding:6px 14px; font-size:12px; background:#243824;
          color:#aabbaa; border:1px solid #3a6a3a; border-radius:4px; cursor:pointer; font-family:Arial,sans-serif;">← Back</button>
        ${isHost && t.status === 'active' ? `<button id="tlEndEarlyBtn" style="padding:6px 14px; font-size:12px; background:#3a2424;
          color:#e0aaaa; border:1px solid #6a3a3a; border-radius:4px; cursor:pointer; font-family:Arial,sans-serif;">End Tournament Early</button>` : ''}
      </div>
    </div>`;
  }

  private renderRoom(): void {
    (this.form.node as HTMLElement).innerHTML = this.buildRoomHTML();
  }
}

function sideIcon(side: Side): string {
  return side === 'allies' ? '🔵' : '🔴';
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
